import {
  Box,
  CircularProgress,
  Container,
  Divider,
  Skeleton,
  Stack,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { firstBy } from "thenby";

import { dayjs } from "@thalamos/common";
import { triggerDownload } from "@thalamos/frontend";
import type {
  ExtendedVisionUser,
  GetIncidentWorkItemResponse,
  IncidentWorkItemWithContext,
} from "@thalamos/vision-common";
import {
  buildIncidentFormPdfFilename,
  calculateActions,
  defaultActions,
  getIncidentTypeSelection,
  tk,
} from "@thalamos/vision-common";

import api, { apiHooks } from "../../api/index.js";
import { Banner } from "../../components/Banner/Banner.js";
import {
  IncidentWorkItemCard,
  TypographyI18N,
} from "../../components/index.js";
import { useUser } from "../../Context/UserContext.js";
import { Error as ErrorComponent } from "../Error/Error.js";
import { DesktopArchiveTable } from "./DesktopArchiveTable/DesktopArchiveTable.js";

async function downloadPdf(
  incident: IncidentWorkItemWithContext,
): Promise<void> {
  try {
    const response = await api.incidentWorkItem.getPdf(
      incident.incidentWorkItem.id,
    );

    if (response.status !== 200) {
      console.error("Failed to download PDF. Response:", response);
      return;
    }

    const dataUri = `data:application/pdf;base64,${response.data}`;
    triggerDownload(
      dataUri,
      buildIncidentFormPdfFilename({
        incidentType: getIncidentTypeSelection({
          incidentWorkItem: incident,
        }).unwrapOr(undefined),
        date: new Date(),
      }),
    );
  } catch (error) {
    console.error("Error downloading PDF:", error);
  }
}

export function ArchivePage() {
  const { user, loading: loadingUser, error: userError } = useUser();
  const [
    {
      data: incidentWorkItems,
      loading: loadingIncidentWorkItems,
      error: incidentWorkItemsError,
    },
  ] = apiHooks.incidentWorkItem.getArchiveForms(user?.id);

  const loading = loadingUser || loadingIncidentWorkItems;
  const error = userError || incidentWorkItemsError;

  if (error) {
    return <ErrorComponent />;
  }

  if (!user || !user.sessionContext) {
    return <CircularProgress />;
  }

  return (
    <ArchivePageInner
      archiveData={incidentWorkItems}
      user={user}
      loading={loading}
      onDownloadPdf={downloadPdf}
    />
  );
}

export function ArchivePageInner({
  archiveData,
  user,
  loading,
  onDownloadPdf,
}: {
  archiveData: GetIncidentWorkItemResponse | undefined;
  user: ExtendedVisionUser | undefined;
  loading: boolean;
  onDownloadPdf: (incident: IncidentWorkItemWithContext) => Promise<void>;
}) {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));

  const incidents = Array.isArray(archiveData?.workItems)
    ? (archiveData.workItems as IncidentWorkItemWithContext[])
    : [];

  const sortedIncidents = [...incidents].sort(
    firstBy((incident) => incident.incidentWorkItem.createdAt, "desc"),
  );

  if (isDesktop) {
    return (
      <Stack
        data-testid="archive-page"
        sx={{
          padding: "5rem",
          width: "100%",
          display: "flex",
          flexDirection: "column",
          gap: "2rem",
        }}
      >
        <TypographyI18N
          variant="h4"
          component="div"
          gutterBottom
          translationKey={tk("archivePage.title")}
        />
        <Banner
          body={tk("archivePage.endedFormsAvailablity")}
          bannerType={"warning"}
        />
        <Divider />
        <DesktopArchiveTable
          incidents={sortedIncidents}
          user={user}
          isLoading={loading}
          onDownloadPdf={onDownloadPdf}
        />
      </Stack>
    );
  }

  // ---- Mobile layout ----

  if (loading) {
    return (
      <Container
        data-testid="archive-skeleton"
        maxWidth="md"
        sx={{ marginTop: "40px" }}
      >
        <Skeleton variant="text" width="100%" height={40} />
        <Skeleton
          variant="rectangular"
          width="100%"
          height={200}
          sx={{ mt: 2 }}
        />
      </Container>
    );
  }

  if (incidents.length === 0) {
    return (
      <Container maxWidth="md" sx={{ marginTop: "40px", textAlign: "center" }}>
        <Box
          sx={{
            padding: "1rem",
            borderRadius: "10px",
            boxShadow: "0px 4px 10px rgba(0,0,0,0.1)",
          }}
        >
          <TypographyI18N
            variant="h4"
            component="div"
            gutterBottom
            translationKey={tk("archivePage.noFormsCompleted")}
          />
          <TypographyI18N
            variant="body1"
            translationKey={tk("archivePage.noFormsCompletedMessage")}
          />
        </Box>
      </Container>
    );
  }

  return (
    <Stack
      data-testid="archive-page"
      sx={{
        padding: "2rem",
        width: "100%",
        display: "flex",
        flexDirection: "column",
        gap: "2rem",
      }}
    >
      <TypographyI18N
        variant="h4"
        component="div"
        gutterBottom
        translationKey={tk("archivePage.title")}
      />
      <Banner
        body={tk("archivePage.endedFormsAvailablity")}
        bannerType={"warning"}
      />
      <Divider />
      {sortedIncidents.map((incident) => {
        const actions = user
          ? calculateActions({
              incidentWorkItem: incident,
              user,
              asOfDateTime: dayjs(),
            })
          : defaultActions;

        return (
          <IncidentWorkItemCard
            key={incident.incidentWorkItem.id}
            incidentWorkItem={incident}
            primaryButton={{
              label: tk("common.download"),
              disabled: !actions.pdf.canDownload,
              onClick: () => {
                void onDownloadPdf(incident);
              },
            }}
            showAssignedOfficerOnMobile={true}
          />
        );
      })}
    </Stack>
  );
}
